'use client';

import {
  type inferParserType,
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  throttle,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { z } from 'zod';

import { generalConfig } from '@sola/config/lib/getters.js';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';

import { ArticleTabsEnum } from '../molecules/article-group/article-term-list';

const limitValues = ['4', '8', '12', '24'] as const;
export const ArticleLimitEnum = z.enum(limitValues);
export type ArticleLimitEnum = z.infer<typeof ArticleLimitEnum>;

const giftCatalogSortValues = [
  'mostNeeded',
  'priceLowToHigh',
  'priceHighToLow',
  'alphabeticalAZ',
  'alphabeticalZA',
] as const;
export const GiftCatalogSortEnum = z.enum(giftCatalogSortValues);
export type GiftCatalogSortEnum = z.infer<typeof GiftCatalogSortEnum>;

const sortByValues = ['promotedContent', 'desc', 'asc'] as const;
export const ArticleSortByEnum = z.enum(sortByValues);
export type ArticleSortByEnum = z.infer<typeof ArticleSortByEnum>;

const sacFilterValues = [
  'orphanedChild',
  'hasDisabilities',
  'vulnerableExploitation',
  'affectedHiv',
] as const;
export const SacFiltersEnum = z.enum(sacFilterValues);
export type SacFiltersEnum = z.infer<typeof SacFiltersEnum>;

export const usePaginationQueryParams = () => {
  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(0).withOptions({ scroll: true }),
  );

  const [currentPageWithoutScroll, setCurrentPageWithoutScroll] = useQueryState(
    'page',
    parseAsInteger.withDefault(0),
  );

  return {
    currentPage,
    setCurrentPage,
    currentPageWithoutScroll,
    setCurrentPageWithoutScroll,
  };
};

export const useSearchQueryParams = () => {
  const [searchFilters, setSearchFilters] = useQueryState(
    'q',
    parseAsString.withDefault(''),
  );

  return { searchFilters, setSearchFilters };
};

export const giftCatalogParamsSchema = {
  category: parseAsArrayOf(parseAsString, ',').withDefault([]),
  promotion: parseAsString.withDefault(''),
  priceMin: parseAsInteger.withDefault(generalConfig.catalogFilterMin),
  priceMax: parseAsInteger.withDefault(generalConfig.catalogFilterMax),
  sortby: parseAsString.withDefault(GiftCatalogSortEnum.enum.mostNeeded),
};

export const useGiftCatalogQueryParams = () => {
  const [giftCatalogFilters, setGiftCatalogFilters] = useQueryStates(
    giftCatalogParamsSchema,
  );

  return {
    giftCatalogFilters,
    setGiftCatalogFilters,
  };
};

export const useTabGroupQueryParams = () => {
  const [tabGroupFilters, setTabGroupFilters] = useQueryState(
    'tab',
    parseAsString.withDefault(ArticleTabsEnum.enum.all),
  );

  return { tabGroupFilters, setTabGroupFilters };
};

export type ArticleQueryParams = {
  limit?: number;
  sortby?: ArticleSortByEnum;
  topic?: string;
  theme?: string;
  country?: string;
};

export const useArticleQueryParams = (props?: { perPage?: number }) => {
  const { perPage } = props || {};
  const articleQueryParamsSchema = {
    limit: parseAsInteger.withDefault(perPage || 12),
    sortby: parseAsString.withDefault(ArticleSortByEnum.enum.desc),
    topic: parseAsString,
    theme: parseAsString,
    country: parseAsString,
  };

  const [articleFilters, setArticleFilters] = useQueryStates(
    articleQueryParamsSchema,
  );

  return { articleFilters, setArticleFilters };
};

const { sacMinAge, sacMaxAge } = generalConfig;

export const sacQueryParamsSchema = {
  gender: parseAsStringEnum<ChildGenderEnum>(
    Object.values(ChildGenderEnum),
  ).withDefault(ChildGenderEnum.Either),
  countries: parseAsArrayOf(parseAsString.withDefault('')).withDefault([]),
  ageMin: parseAsInteger.withDefault(sacMinAge),
  ageMax: parseAsInteger.withDefault(sacMaxAge),
  birthDay: parseAsInteger.withDefault(0),
  birthMonth: parseAsInteger.withDefault(0),
  filters: parseAsArrayOf(parseAsString.withDefault(''), '%2C').withDefault([]),
};

export type SacQueryParams = inferParserType<typeof sacQueryParamsSchema>;

export const useSacQueryParams = ({ isLive }: { isLive?: boolean }) => {
  const [sacFilters, setSacFilters] = useQueryStates(sacQueryParamsSchema, {
    history: 'replace',
    limitUrlUpdates: isLive ? undefined : throttle(Infinity),
  });

  return {
    sacFilters,
    setSacFilters,
  };
};

export const fundQueryParamsSchema = {
  oneTime: parseAsArrayOf(parseAsInteger),
  monthly: parseAsArrayOf(parseAsInteger),
  annually: parseAsArrayOf(parseAsInteger),
  defaultFrequency: parseAsStringEnum(
    Object.values(CommitmentFrequencyEnum) as CommitmentFrequencyEnum[],
  ),
  defaultOneTime: parseAsInteger,
  defaultMonthly: parseAsInteger,
  defaultAnnually: parseAsInteger,
};

export type FundQueryParams = inferParserType<typeof fundQueryParamsSchema>;

export const useFundQueryParams = () => {
  const [fundParams, setFundParams] = useQueryStates(fundQueryParamsSchema);
  return {
    fundParams,
    setFundParams,
  };
};
